import { createContext, useContext, useState, useReducer, useMemo, useEffect, createElement, useCallback } from 'react';
import { Loader } from '@grandvision/common';
import { Container, Heading } from '@grandvision/elements';
import { createScriptTag, injectTags } from '@grandvision/utils';
import { actions as actions$1, reducer, useIntl } from '@grandvision/state';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0
THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.
See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}
function __spread() {
    for (var ar = [], i = 0; i < arguments.length; i++)
        ar = ar.concat(__read(arguments[i]));
    return ar;
}

/**
 * Maps our settings to an object that's accepted by PowerReviews.display.render() method
 */
var createPowerReviewsConfig = function (_a) {
    var _b;
    var config = _a.config, productId = _a.productId, variantId = _a.variantId, type = _a.type, id = _a.id, returnUrl = _a.returnUrl, onRender = _a.onRender, onSubmit = _a.onSubmit;
    return (__assign(__assign(__assign(__assign(__assign({ enable_content_collection_modal: false, api_key: config.apiKey, locale: config.locale.replace('-', '_'), merchant_group_id: config.groupId, merchant_id: config.opcoId, 
        // Append the "?" as PowerReviews doesn't do that but will add query params.
        review_wrapper_url: config.writeReviewPath + "?", components: (_b = {},
            _b[type] = id,
            _b) }, (productId && { page_id: productId })), (variantId && { page_id_variant: variantId })), (returnUrl && { return_url: returnUrl })), (onRender && { on_render: onRender })), (onSubmit && { on_submit: onSubmit })));
};
/**
 * PowerReviews doesn't accept multiple widgets to be loaded separately.
 * It always expects one object instead of an array or multiple calls to the render fn.
 */
var mergeConfigs = function (configs) {
    return configs.reduce(function (result, next) { return (__assign(__assign(__assign({}, result), next), { components: __assign(__assign({}, result.components), next.components) })); }, {});
};

var loadReviewWrite = actions$1.action('LOAD_REVIEW_WRITE', actions$1.payload());
var loadReviewSnippet = actions$1.action('LOAD_REVIEW_SNIPPET', actions$1.payload());
var loadReviewDisplay = actions$1.action('LOAD_REVIEW_DISPLAY', actions$1.payload());
var updateReviewDisplay = actions$1.action('UPDATE_REVIEW_DISPLAY', actions$1.payload());

var actions = /*#__PURE__*/Object.freeze({
  __proto__: null,
  loadReviewWrite: loadReviewWrite,
  loadReviewSnippet: loadReviewSnippet,
  loadReviewDisplay: loadReviewDisplay,
  updateReviewDisplay: updateReviewDisplay
});

var initialState = {
    reviewWrite: null,
    reviewSnippet: null,
    reviewDisplay: null,
};
var reviewsReducer = reducer(initialState, 
/**
 * Load widgets
 */
actions$1.on(loadReviewWrite, function (state, action) { return (__assign(__assign({}, state), { reviewWrite: action.payload })); }), actions$1.on(loadReviewSnippet, function (state, action) { return (__assign(__assign({}, state), { reviewSnippet: action.payload })); }), actions$1.on(loadReviewDisplay, function (state, action) { return (__assign(__assign({}, state), { reviewDisplay: action.payload })); }), 
/**
 * Update widgets
 */
actions$1.on(updateReviewDisplay, function (state, action) {
    return state.reviewDisplay !== null
        ? __assign(__assign({}, state), { reviewDisplay: __assign(__assign({}, state.reviewDisplay), { variantId: action.payload.variantId }) }) : state;
}));

// These are the keys PowerReviews requires in the `components` object when initializing a widget
var mapStateToPowerReviewsType = {
    reviewWrite: 'Write',
    reviewSnippet: 'ReviewSnippet',
    reviewDisplay: 'ReviewDisplay',
};
var ReviewsContext = createContext({});
var useReviewsContext = function () { return useContext(ReviewsContext); };
var PR_SCRIPT_ID = 'power-reviews-script';
var PR_SCRIPT_SRC = 'https://assets.grandvision.io/global/power-reviews-stable-4.0-ui.js';
var ReviewsProvider = function (_a) {
    var enabled = _a.enabled, config = _a.config, children = _a.children;
    var _b = __read(useState(false), 2), isInitialized = _b[0], setIsInitialized = _b[1];
    var _c = __read(useReducer(reviewsReducer, initialState), 2), state = _c[0], dispatch = _c[1];
    var widgets = useMemo(function () {
        return Object.keys(state).reduce(function (result, key) {
            if (state[key] !== null) {
                var widgetConfig = createPowerReviewsConfig(__assign(__assign({ config: config }, state[key]), { type: mapStateToPowerReviewsType[key] }));
                return __spread(result, [widgetConfig]);
            }
            return result;
        }, []);
    }, [state]);
    useEffect(function () {
        var scriptLoaded = !!(document === null || document === void 0 ? void 0 : document.getElementById(PR_SCRIPT_ID));
        if (enabled && !scriptLoaded && !isInitialized) {
            var script = createScriptTag({
                id: PR_SCRIPT_ID,
                src: PR_SCRIPT_SRC,
                defer: true,
                cb: function () { return setIsInitialized(true); },
            });
            injectTags([script]);
        }
    }, [enabled, isInitialized]);
    useEffect(function () {
        if (!isInitialized) {
            return;
        }
        if (!config || !window.POWERREVIEWS) {
            console.error("Failed to initialize PowerReviews widgets with config: " + config + " and state: " + state);
            return;
        }
        if (widgets.length) {
            // Clear existing widgets before (re)initializing
            Object.keys(state).forEach(function (key) { return state[key] !== null && clearWidgetHtml(state[key].id); });
            // PowerReviews doesn't accept an array of configs when rendering multiple widgets
            window.POWERREVIEWS.display.render(widgets.length > 1 ? mergeConfigs(widgets) : widgets[0]);
        }
    }, [widgets, config, isInitialized]);
    var value = useMemo(function () { return ({
        enabled: enabled,
        config: config,
        dispatch: dispatch,
    }); }, [enabled, config]);
    return createElement(ReviewsContext.Provider, { value: value }, children);
};
// Because PowerReviews doesn't clear their widget's HTML themselves
// when we re-call their `display` method
function clearWidgetHtml(id) {
    var element = document.getElementById(id);
    if (element) {
        element.innerHTML = '';
    }
}

var REVIEW_DISPLAY_ID = 'pr-review-display';
var ReviewDisplay = function (_a) {
    var productId = _a.productId, variantId = _a.variantId;
    var _b = useReviewsContext(), enabled = _b.enabled, dispatch = _b.dispatch;
    useEffect(function () {
        if (enabled && productId) {
            dispatch(loadReviewDisplay({
                id: REVIEW_DISPLAY_ID,
                productId: productId,
                variantId: variantId,
            }));
        }
    }, [enabled, productId, variantId]);
    if (!enabled) {
        return null;
    }
    return (createElement("div", { className: "review-display", "data-t": "review-display" },
        createElement(Container, null,
            createElement("div", { id: REVIEW_DISPLAY_ID },
                createElement(Loader, null)))));
};

var REVIEW_SNIPPET_ID = 'pr-review-snippet';
var ReviewSnippet = function (_a) {
    var productId = _a.productId, variantId = _a.variantId;
    var _b = useReviewsContext(), enabled = _b.enabled, dispatch = _b.dispatch;
    useEffect(function () {
        if (enabled && productId) {
            dispatch(loadReviewSnippet({
                id: REVIEW_SNIPPET_ID,
                productId: productId,
                variantId: variantId,
            }));
        }
    }, [enabled, productId, variantId]);
    if (!enabled) {
        return null;
    }
    return (createElement("div", { className: "review-snippet", "data-t": "review-snippet" },
        createElement("div", { id: REVIEW_SNIPPET_ID })));
};

var REVIEW_WRITE_ID = 'pr-review-write';
var ReviewWrite = function (_a) {
    var selfUrl = _a.selfUrl;
    var getLabel = useIntl().getLabel;
    var _b = useReviewsContext(), enabled = _b.enabled, dispatch = _b.dispatch;
    var _c = __read(useState(false), 2), submitted = _c[0], setSubmitted = _c[1];
    var onSubmit = useCallback(function () {
        setSubmitted(true);
        window && window.scrollTo(0, 0);
    }, []);
    useEffect(function () {
        if (enabled) {
            dispatch(loadReviewWrite({
                id: REVIEW_WRITE_ID,
                returnUrl: selfUrl,
                onSubmit: onSubmit,
            }));
        }
    }, [enabled, selfUrl, onSubmit]);
    if (!enabled) {
        return null;
    }
    return (createElement("div", { className: "review-write", "data-t": "review-write" },
        createElement(Container, null,
            !submitted && (createElement(Heading, { variant: "small", component: "h1" }, getLabel('reviews.write.title'))),
            createElement("div", { id: REVIEW_WRITE_ID }))));
};

export { actions as Actions, PR_SCRIPT_ID, PR_SCRIPT_SRC, ReviewDisplay, ReviewSnippet, ReviewWrite, ReviewsProvider, mapStateToPowerReviewsType, useReviewsContext };
//# sourceMappingURL=index.es.js.map
